'use client'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { signUp as passwordSignup } from 'supertokens-web-js/recipe/emailpassword'
import { FormButton } from '../Form/FormButton'
import { FormInput } from '../Form/FormInput'
import { PasswordInput } from '../Form/PasswordInput'

type EmailSignupFormData = {
  name: string
  email: string
  password: string
  confirmPassword: string
}

type SignUpFormProps = {
  onSignUpSuccessAction?: (method: string, isSignUp?: boolean) => void
  onSubmitStart?: (method: string) => void
  onError?: (error: string, method: string) => void
}

export default function SignUpForm({
  onSignUpSuccessAction,
  onSubmitStart,
  onError,
}: SignUpFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EmailSignupFormData>()

  const [apiFieldErrors, setApiFieldErrors] = useState<
    Omit<EmailSignupFormData, 'confirmPassword'>
  >({
    email: '',
    password: '',
    name: '',
  })

  const onSubmit = async ({ email, password, name }: EmailSignupFormData) => {
    onSubmitStart?.('email')
    try {
      const response = await passwordSignup({
        formFields: [
          { id: 'email', value: email },
          { id: 'password', value: password },
          { id: 'name', value: name },
        ],
      })

      switch (response.status) {
        case 'OK':
          onSignUpSuccessAction?.('email', true)
          break
        case 'SIGN_UP_NOT_ALLOWED':
          toast.error(response.reason)
          onError?.(response.reason, 'email')
          break
        case 'FIELD_ERROR':
          setApiFieldErrors({
            email: response.formFields.find((field) => field.id === 'email')?.error || '',
            password: response.formFields.find((field) => field.id === 'password')?.error || '',
            name: response.formFields.find((field) => field.id === 'name')?.error || '',
          })
          onError?.('Field validation error', 'email')
          break
      }
    } catch (error) {
      toast.error((error as Error).message)
      onError?.((error as Error).message, 'email')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-[32px]">
      <div className="flex flex-col gap-[9px]">
        <FormInput
          type="text"
          placeholder="Name"
          autoComplete="name"
          {...register('name', { required: 'Name is required' })}
          error={errors.name?.message}
          apiError={apiFieldErrors.name}
        />

        <FormInput
          type="email"
          placeholder="Email"
          autoComplete="email"
          {...register('email', { required: 'Email is required' })}
          error={errors.email?.message}
          apiError={apiFieldErrors.email}
        />

        <PasswordInput
          placeholder="Password"
          autoComplete="new-password"
          {...register('password', { required: 'Password is required' })}
          error={errors.password?.message}
          apiError={apiFieldErrors.password}
        />

        <PasswordInput
          placeholder="Confirm Password"
          autoComplete="new-password"
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (val: string) => {
              if (watch('password') != val) {
                return "Passwords don't match"
              }
            },
          })}
          error={errors.confirmPassword?.message}
        />
      </div>

      <FormButton type="submit" className="w-full">
        Create Account
      </FormButton>
    </form>
  )
}
